/**
 * DECIDE — the minimal decision-recording surface on the site's Decision tab
 * (or, when the split Decision surfaces are rolled back, under Analyses).
 *
 * Path to GO removal, stage 1: decisions become recordable where the decision
 * basis is already read, instead of only inside the Path to GO wizard. This
 * surface composes the SAME pieces the Path to GO Decide phase renders — no
 * second implementation:
 *   - what is standing in the way (DecisionBlockerList, the Resolve list),
 *     plus — while GO / CONDITIONAL are locked — which prerequisite phases
 *     (decision intent, required analyses, verification) still hold them,
 *     with the Decide phase's own `lockedReason` and a route to each,
 *   - what lifts the band (exact `projectPillarShift` rows),
 *   - the record form (DecisionRecordForm),
 *   - the immutable history with frozen evidence, author, rejection reason and
 *     the approver review controls (DecisionHistoryList).
 *
 * It is presentational. Every write goes through the host's handlers, which
 * are the SAME SiteLandingPage handlers the Path to GO wizard used (the wizard
 * was deleted in stage 3; the handlers stayed) — they hold
 * the lock check and the positive-decision gate; the server re-checks both.
 * `state` MUST be the page-level Path-to-GO state (no document coverage
 * matching), i.e. exactly the state those handlers gate on, so the form's
 * disabled GO / CONDITIONAL options can never disagree with the gate.
 *
 * The record form is held until the decision history has loaded: a new record
 * supersedes the current one on the server, so offering the form against an
 * unknown (loading or failed) history would let a user supersede a decision
 * they never saw. Everything shown here is display / navigation only — none
 * of it feeds a gate.
 */
import React from 'react';
import { Eye, Loader2, RotateCw } from 'lucide-react';
import { isResolveBlocker } from '../../../lib/pathToGo/blockers';
import { pathToGoPositiveDecisionAllowed } from '../../../lib/pathToGo/decisionSnapshot';
import type { NextStep } from '../../../lib/pathToGo/nextStep';
import type {
  PathToGoPhase,
  PathToGoPhaseId,
  PathToGoState,
} from '../../../lib/pathToGo/pathToGoOrchestrator';
import type { DecisionBandConfig } from '../../../lib/portfolio/decisionBands';
import type { SiteComposite } from '../../../types/portfolioComposite';
import type { PathToGoDecisionRecord, PathToGoDecisionEvidenceSnapshot, PathToGoDecisionStage } from '../../../types/pathToGoDecision';
import { screeningPositiveDecisionAllowed } from '../../../lib/pathToGo/screeningDecisionSnapshot';
import { ScreeningDecisionRecordForm, type ScreeningDecisionDraft } from './ScreeningDecisionRecordForm';
import type { SaveAmendmentResult, SEDAmendment } from '../../../types/sedAmendments';
import type { SiteEvidenceDocument } from '../../../types/siteEvidence';
import { DecisionBlockerList } from './DecisionBlockerList';
import { DecisionHistoryList } from './DecisionHistoryList';
import { DecisionLiftList } from './DecisionLiftList';
import {
  DecisionRecordForm,
  type PathToGoDecisionDraft,
} from './DecisionRecordForm';
import { computeLiftRows, decisionBandIdForVerdict } from './decisionLift';

/** DOM id of the surface — the Decision tab's "Review and record" CTA scrolls here. */
export const DECIDE_SURFACE_ID = 'decide-surface';

/** DOM id of the surface heading — the focus fallback when no field is editable. */
export const DECIDE_SURFACE_TITLE_ID = 'decide-surface-title';

/**
 * State of the host's decision-history read. Only `loaded` is a claim about
 * the audit trail; the record form is offered only then.
 */
export type DecisionHistoryStatus = 'loading' | 'loaded' | 'failed';

/** The phases that must clear before GO / CONDITIONAL (every phase but Decide). */
export type DecidePrerequisitePhaseId = Exclude<PathToGoPhaseId, 'decide'>;

/** A host route to the work of one prerequisite phase. */
export interface DecidePrerequisiteRoute {
  label: string;
  onOpen: () => void;
}

const PREREQUISITE_ORDER: readonly DecidePrerequisitePhaseId[] = ['define', 'build', 'verify'];

const PREREQUISITE_TITLE: Record<DecidePrerequisitePhaseId, string> = {
  define: 'Decision intent',
  build: 'Required analyses',
  verify: 'Verification',
};

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

/**
 * What is still open in one prerequisite phase, in the orchestrator's own
 * counts. `verify` includes the resolve-class blockers (`countResolveBlockers`),
 * which are already listed above with their own routes, so they are named as
 * "listed above" rather than counted as Site-evidence work.
 */
function prerequisiteDetail(
  phase: PathToGoPhase,
  openBlockerCount: number,
): { detail: string; hasRoutableWork: boolean } {
  if (phase.id === 'define') {
    const parts: string[] = [];
    if (phase.openCount > 0) {
      parts.push(`${plural(phase.openCount, 'decision-defining answer', 'decision-defining answers')} to confirm`);
    }
    if ((phase.followUpCount ?? 0) > 0) {
      parts.push(`${plural(phase.followUpCount ?? 0, 'answer', 'answers')} recorded as unknown (follow-up open)`);
    }
    return { detail: parts.join(' · ') || 'Not yet confirmed', hasRoutableWork: true };
  }
  if (phase.id === 'build') {
    return {
      detail: phase.openCount > 0
        ? `${plural(phase.openCount, 'required analysis', 'required analyses')} not yet current`
        : 'Not yet current',
      hasRoutableWork: true,
    };
  }
  const conflicts = Math.min(openBlockerCount, phase.openCount);
  const other = phase.openCount - conflicts;
  const parts: string[] = [];
  if (other > 0) parts.push(`${plural(other, 'verification item', 'verification items')} open`);
  if (conflicts > 0) parts.push(`${plural(conflicts, 'conflict', 'conflicts')} listed above`);
  return {
    detail: parts.join(' · ') || 'Not yet current',
    // Conflicts-only: the blocker rows above carry the routes.
    hasRoutableWork: other > 0 || conflicts === 0,
  };
}

export interface DecideSurfaceProps {
  /** Selected review stage; absence preserves the legacy final-decision surface. */
  decisionStage?: PathToGoDecisionStage;
  screeningEvidence?: PathToGoDecisionEvidenceSnapshot | null;
  /** Eligible generated Stage 2 report IDs for score display only; absence
   * with an explicit Stage 2 review withholds unproven score rows. */
  validationReportIds?: readonly string[];
  onRecordScreeningDecision?: (draft: ScreeningDecisionDraft) => void | Promise<void>;
  /** Page-level Path-to-GO state — the state the decision handlers gate on. */
  state: PathToGoState;
  /** The oracle composite, for the EXACT lift projections. */
  composite: SiteComposite | null;
  bandConfig?: DecisionBandConfig | null;
  /**
   * True while reports or the current SED are still loading. The gate would be
   * computed from a partial record, so the surface holds (as the deleted Path
   * to GO wizard did) instead of offering a decision against it.
   */
  loading?: boolean;
  /** Immutable history, newest first. Read only when `historyStatus` is `loaded`. */
  decisionRecords: readonly PathToGoDecisionRecord[];
  /**
   * The host's history read. `loading` / `failed` hold the record form and
   * make no claim about what is on record.
   */
  historyStatus: DecisionHistoryStatus;
  /** Re-run the history read after a failure. Absent → no retry control. */
  onRetryHistory?: () => void;
  /** Host handler (lock check + positive gate + persist). Absent → no form. */
  onRecordDecision?: (draft: PathToGoDecisionDraft) => void | Promise<void>;
  /** Host handler, passed ONLY to reviewers (role gate lives in the host). */
  onReviewDecision?: (
    recordId: string,
    outcome: 'approved' | 'rejected',
    rejectionReason?: string,
  ) => void | Promise<void>;
  decisionOwnerDefault?: string;
  /** True for a locked site's non-holder: every control is disabled. */
  decisionReadOnly?: boolean;
  onRequestEditAccess?: () => void;
  /* ── "Standing in the way" routing (all optional) ── */
  onResolveStep?: (step: NextStep) => void;
  /**
   * Routes to the work of each prerequisite phase that still holds GO /
   * CONDITIONAL. A phase with no route renders its row without a button.
   */
  prerequisiteRoutes?: Partial<Record<DecidePrerequisitePhaseId, DecidePrerequisiteRoute>>;
  onOpenAudit?: () => void;
  onOpenAnalyses?: () => void;
  sed?: SiteEvidenceDocument | null;
  onResolveInPlace?: (amendment: SEDAmendment) => Promise<SaveAmendmentResult>;
  toast?: (msg: string) => void;
  className?: string;
}

export const DecideSurface: React.FC<DecideSurfaceProps> = ({
  state,
  decisionStage,
  screeningEvidence,
  validationReportIds,
  onRecordScreeningDecision,
  composite,
  bandConfig,
  loading = false,
  decisionRecords,
  historyStatus,
  onRetryHistory,
  onRecordDecision,
  onReviewDecision,
  decisionOwnerDefault,
  decisionReadOnly = false,
  onRequestEditAccess,
  onResolveStep,
  prerequisiteRoutes,
  onOpenAudit,
  onOpenAnalyses,
  sed,
  onResolveInPlace,
  toast,
  className,
}) => {
  const screening = decisionStage === 1;
  const stageRecords = decisionRecords.filter(record => (record.decisionStage ?? 2) === (decisionStage ?? 2));
  const { verdict } = state;
  const isGo = verdict.dataAdequate && verdict.band === 'go';
  const recommendationWithheld = verdict.band == null;
  const currentBandId = decisionBandIdForVerdict(verdict);
  const validationIds = React.useMemo(() => new Set(validationReportIds ?? []), [validationReportIds]);
  const validationScoreReview = decisionStage === 2;
  const compositeHasValidationSources = Boolean(composite
    && composite.sourceReportIds.length > 0
    && composite.sourceReportIds.every(id => validationIds.has(id)));
  const displayedBlockers = state.blockers.filter(blocker => {
    if (!validationScoreReview) return true;
    if (blocker.id === 'band:no-go') return compositeHasValidationSources;
    if (!blocker.id.startsWith('red-pillar:')) return true;
    const pillarId = blocker.id.slice('red-pillar:'.length);
    const pillar = composite && Object.values(composite.pillars).find(row => row.pillarId === pillarId);
    return Boolean(pillar && !pillar.missing && pillar.sourceReportIds.length > 0
      && pillar.sourceReportIds.every(id => validationIds.has(id)));
  });
  const withheldScoreBlockers = state.blockers.length - displayedBlockers.length;
  // The oracle projects the whole composite. A validation-only pillar does
  // not make a band projection validation-only when other sources are screening.
  const validationProjectionHeld = validationScoreReview && !compositeHasValidationSources;
  const liftRows = React.useMemo(() => {
    if (!composite || !currentBandId || isGo || validationProjectionHeld) return [];
    return computeLiftRows(composite, currentBandId, bandConfig);
  }, [composite, currentBandId, isGo, bandConfig, validationProjectionHeld]);
  const positiveDecisionGateSatisfied = screening
    ? Boolean(screeningEvidence && screeningPositiveDecisionAllowed(screeningEvidence))
    : pathToGoPositiveDecisionAllowed(state);
  const openBlockerCount = displayedBlockers.filter(isResolveBlocker).length;
  // What still holds GO / CONDITIONAL — rendered ONLY while the gate is
  // closed. Display only: the gate itself is `pathToGoPositiveDecisionAllowed`.
  const decideLockedReason = state.phases.find(phase => phase.id === 'decide')?.lockedReason;
  const openPrerequisites = PREREQUISITE_ORDER
    .map(id => state.phases.find(phase => phase.id === id))
    .filter((phase): phase is PathToGoPhase => phase != null && phase.status !== 'cleared');

  return (
    <section
      id={DECIDE_SURFACE_ID}
      className={`scroll-mt-4 rounded-lg border border-border bg-card shadow-card ${className ?? ''}`.trim()}
      aria-labelledby={DECIDE_SURFACE_TITLE_ID}
      data-testid="decide-surface"
      aria-busy={loading || historyStatus === 'loading'}
      data-positive-gate={positiveDecisionGateSatisfied ? 'open' : 'closed'}
      data-history={historyStatus}
      data-decision-stage={decisionStage ?? 2}
    >
      <div className="flex flex-col gap-2 border-b border-border-subtle px-4 py-3.5 sm:flex-row sm:items-start sm:justify-between md:px-5">
        <div className="min-w-0">
          <p className="type-meta text-primary">Decide</p>
          <h2
            id={DECIDE_SURFACE_TITLE_ID}
            tabIndex={-1}
            className="mt-0.5 text-sm font-bold text-text-primary focus:outline-none"
          >
            {screening ? 'Record the screening decision' : decisionStage === 2 ? 'Record the final decision' : 'Record the accountable decision'}
          </h2>
          <p className="mt-1 max-w-2xl text-xs leading-relaxed text-text-muted">
            {screening
              ? 'Record whether the saved screening supports proceeding to validation. An approver reviews the submitted screening decision; the final decision is recorded separately in Stage 2.'
              : 'NO-GO and DEFER can be recorded at any time. GO and CONDITIONAL unlock when the decision intent, required analyses, and verification work are current. An approver reviews each submitted final decision.'}
          </p>
          {sed?.demonstrationProvenance?.kind === 'fictional-scenario' && <p className="mt-1 max-w-2xl text-xs leading-relaxed text-text-muted" data-testid="decide-hypothetical-scope">
            Hypothetical scenario {sed.demonstrationProvenance.scenarioId}; the calculated evidence and recorded decision test the workflow and do not authorize a real-world investment or commitment.
          </p>}
        </div>
        {decisionReadOnly && (
          <div className="flex shrink-0 flex-wrap items-center gap-2" data-testid="decide-read-only">
            <span className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-border px-3 type-meta text-text-secondary">
              <Eye className="h-3.5 w-3.5" aria-hidden="true" />
              Read-only review
            </span>
            {onRequestEditAccess && (
              <button
                type="button"
                onClick={onRequestEditAccess}
                className="min-h-11 rounded-lg border border-border px-3 text-xs font-bold text-text-secondary hover:bg-surface-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                data-testid="decide-request-access"
              >
                Request edit access
              </button>
            )}
          </div>
        )}
      </div>

      {loading ? (
        <div className="p-4 md:p-5">
          <div
            className="flex min-h-11 items-center gap-3 rounded-lg border border-border-subtle bg-background-elevated px-3 text-sm text-text-secondary"
            role="status"
            aria-live="polite"
            data-testid="decide-loading"
          >
            <Loader2
              className="h-4 w-4 shrink-0 animate-spin text-primary motion-reduce:animate-none"
              aria-hidden="true"
            />
            Loading the current Site Evidence Document before the decision can be recorded…
          </div>
        </div>
      ) : (
      <div className="space-y-4 p-4 md:p-5">
        {!screening && <div className="space-y-2" data-testid="decide-blockers" data-count={openBlockerCount}>
          {validationScoreReview && (validationIds.size === 0 || withheldScoreBlockers > 0) && (
            <p role="status" className="text-xs leading-relaxed text-text-secondary" data-testid="decide-validation-score-status">
              {validationIds.size === 0
                ? 'Awaiting relevant generated validation reports. Screening scores remain available in Stage 1.'
                : 'Screening and unverified scores are excluded from this Stage 2 review. Review screening scores in Stage 1.'}
              {' '}Final decision requirements remain unchanged.
            </p>
          )}
          {openBlockerCount === 0 && withheldScoreBlockers > 0 ? null : openBlockerCount === 0 && positiveDecisionGateSatisfied ? (
            <div
              className="rounded-lg border border-border bg-background-elevated p-3 type-body text-text-secondary"
              data-testid="decide-no-blockers"
            >
              Nothing is standing in the way of the decision.
            </div>
          ) : openBlockerCount === 0 ? (
            // No conflicts, but the gate is closed: say only what is true and
            // let the prerequisite list below name what still holds GO.
            <div
              className="rounded-lg border border-border bg-background-elevated p-3 type-body text-text-secondary"
              data-testid="decide-no-conflicts"
            >
              No conflicts to resolve.
            </div>
          ) : (
            <DecisionBlockerList
              blockers={displayedBlockers}
              onResolveStep={onResolveStep}
              onOpenAudit={onOpenAudit}
              onOpenReports={onOpenAnalyses}
              sed={sed}
              onResolveInPlace={decisionReadOnly ? undefined : onResolveInPlace}
              toast={toast}
              amendmentReasonSource="the Decision tab"
            />
          )}
        </div>}

        {!screening && !positiveDecisionGateSatisfied && (
          <div
            className="rounded-lg border border-warning/40 bg-warning/5 p-3"
            data-testid="decide-prerequisites"
            data-count={openPrerequisites.length}
          >
            <div className="type-meta font-bold text-text-secondary">
              What still holds GO and CONDITIONAL
            </div>
            <p className="mt-1 text-xs text-text-secondary" data-testid="decide-locked-reason">
              {decideLockedReason
                ?? 'GO and CONDITIONAL stay locked until the decision basis is complete.'}
            </p>
            {openPrerequisites.length > 0 && (
              <ul className="mt-2 space-y-1.5">
                {openPrerequisites.map(phase => {
                  const phaseId = phase.id as DecidePrerequisitePhaseId;
                  const { detail, hasRoutableWork } = phase.id === 'verify' && withheldScoreBlockers > 0
                    ? { detail: 'Final verification remains open. Screening scores are reviewed in Stage 1.', hasRoutableWork: true }
                    : prerequisiteDetail(phase, openBlockerCount);
                  const route = hasRoutableWork ? prerequisiteRoutes?.[phaseId] : undefined;
                  return (
                    <li
                      key={phase.id}
                      className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border-subtle bg-background px-2.5 py-1.5"
                      data-testid={`decide-prerequisite-${phase.id}`}
                    >
                      <span className="min-w-0 text-xs">
                        <span className="font-bold text-text-primary">{PREREQUISITE_TITLE[phaseId]}</span>
                        <span className="text-text-secondary"> — {detail}</span>
                      </span>
                      {route && (
                        <button
                          type="button"
                          onClick={route.onOpen}
                          className="inline-flex min-h-11 shrink-0 items-center rounded-lg px-2 type-meta font-bold text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                          data-testid={`decide-prerequisite-open-${phase.id}`}
                        >
                          {route.label} →
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        {!screening && !isGo && (validationProjectionHeld ? (
          <p className="text-xs leading-relaxed text-text-secondary" data-testid="decide-validation-projections-awaiting">
            Band projections await eligible generated validation evidence.
          </p>
        ) : (
          <DecisionLiftList
            liftRows={liftRows}
            recommendationWithheld={recommendationWithheld}
            {...(positiveDecisionGateSatisfied
              // Nothing is listed above when the gate is open, so the shared
              // "closing the open evidence items above" fallback would point
              // at nothing.
              ? { noLiftMessage: 'No single pillar fix changes the band on the current evidence.' }
              : {})}
          />
        ))}

        {!screening && onRecordDecision && historyStatus === 'loaded' && (
          <DecisionRecordForm
            onRecordDecision={onRecordDecision}
            positiveDecisionGateSatisfied={positiveDecisionGateSatisfied}
            initialDecision={isGo ? 'GO' : 'DEFER'}
            decisionOwnerDefault={decisionOwnerDefault}
            decisionReadOnly={decisionReadOnly}
          />
        )}

        {screening && onRecordScreeningDecision && historyStatus === 'loaded' && (
          <ScreeningDecisionRecordForm onRecordDecision={onRecordScreeningDecision}
            positiveDecisionGateSatisfied={positiveDecisionGateSatisfied}
            gateReason={screeningEvidence?.screening?.reason}
            decisionOwnerDefault={decisionOwnerDefault} decisionReadOnly={decisionReadOnly} />
        )}

        {historyStatus === 'loading' ? (
          <div
            className="flex min-h-11 items-center gap-2 border-t border-border-subtle pt-4 text-xs text-text-secondary"
            role="status"
            aria-live="polite"
            data-testid="decide-history-loading"
          >
            <Loader2
              className="h-3.5 w-3.5 shrink-0 animate-spin text-primary motion-reduce:animate-none"
              aria-hidden="true"
            />
            Loading the decision history. The decision form opens once it has loaded.
          </div>
        ) : historyStatus === 'failed' ? (
          <div
            className="space-y-2 border-t border-border-subtle pt-4"
            data-testid="decide-history-failed"
          >
            <p role="alert" className="text-xs text-danger">
              The decision history could not be loaded, so a decision cannot be
              recorded here yet: a new decision would supersede whatever is
              already on record for this site.
            </p>
            {onRetryHistory && (
              <button
                type="button"
                onClick={onRetryHistory}
                className="inline-flex min-h-11 items-center gap-1.5 rounded-lg border border-border px-3 text-xs font-bold text-text-secondary hover:bg-surface-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                data-testid="decide-history-retry"
              >
                <RotateCw className="h-3.5 w-3.5" aria-hidden="true" />
                Try loading the history again
              </button>
            )}
          </div>
        ) : stageRecords.length === 0 ? (
          <p className="border-t border-border-subtle pt-4 text-xs text-text-muted" data-testid="decide-no-records">
            {screening ? 'No Stage 1 screening decision has been recorded for this site yet.' : decisionStage === 2 ? 'No Stage 2 final decision has been recorded for this site yet.' : 'No decision has been recorded for this site yet.'}
          </p>
        ) : (
          <DecisionHistoryList
            decisionRecords={stageRecords}
            onReviewDecision={onReviewDecision}
            decisionReadOnly={decisionReadOnly}
          />
        )}
      </div>
      )}
    </section>
  );
};

export default DecideSurface;
